import type { ReactElement } from 'react';
import type { MetricEntry } from '../../api/metricsTypes.js';
import {
  bmiStatus,
  computeBmi,
  formatTrend,
} from './bmiUtils.js';

export interface BmiCardProps {
  heightCm: number | null;
  latest: MetricEntry | null;
  previous: MetricEntry | null;
}

export function BmiCard({
  heightCm,
  latest,
  previous,
}: BmiCardProps): ReactElement {
  const cardStyle = {
    background: 'var(--bg-surface)',
    borderColor: 'var(--border)',
    color: 'var(--text-main)',
  } as const;

  if (latest === null) {
    return (
      <section
        aria-label="BMI summary"
        data-testid="bmi-card"
        className="rounded-2xl border p-5 shadow-sm"
        style={cardStyle}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold">BMI</span>
        </div>
        <p
          className="mt-3 text-sm"
          style={{ color: 'var(--text-muted)' }}
          data-testid="bmi-empty"
        >
          No measurements yet — tap + Entry to log your first weight.
        </p>
      </section>
    );
  }

  const bmi = computeBmi(latest.weight_kg, heightCm);
  const status = bmi !== null ? bmiStatus(bmi) : null;
  const bmiLabel = bmi !== null ? bmi.toFixed(1) : '—';
  const trend = formatTrend(previous?.weight_kg, latest.weight_kg);
  const heightLabel = heightCm !== null ? `${heightCm} cm` : '— cm';

  return (
    <section
      aria-label="BMI summary"
      data-testid="bmi-card"
      className="rounded-2xl border p-5 shadow-sm"
      style={cardStyle}
    >
      <div className="flex items-center gap-2">
        <span className="text-lg font-semibold">BMI</span>
      </div>

      <div className="mt-3 flex items-baseline gap-3">
        <span
          className="text-3xl font-semibold"
          data-testid="bmi-value"
        >
          {bmiLabel}
        </span>
        {status !== null ? (
          <span
            className="text-sm font-medium"
            style={{ color: 'var(--text-muted)' }}
            data-testid="bmi-status"
          >
            {status}
          </span>
        ) : null}
        {trend !== null ? (
          <span
            className="ml-auto rounded-full border px-2 py-0.5 text-xs font-medium"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
            data-testid="bmi-trend"
          >
            {trend}
          </span>
        ) : null}
      </div>

      <div
        className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm"
        style={{ color: 'var(--text-muted)' }}
      >
        <span data-testid="bmi-height">
          Height: <span style={{ color: 'var(--text-main)' }}>{heightLabel}</span>
        </span>
        <span data-testid="bmi-latest-weight">
          Latest weight:{' '}
          <span style={{ color: 'var(--text-main)' }}>
            {latest.weight_kg.toFixed(1)} kg
          </span>
        </span>
      </div>
    </section>
  );
}
